import { Dropdown, ErrorText, InputField, RadioButton, TableCrossView, TableInput, Text, TextArea } from '../../../components/SecondaryMarket';
import dynamicfunction from '../../../utils/dynamicfunction';
import { fontSizes } from '../../../constants/constants';
import ViewReverse from '../../../components/ViewReverse';
import { Formik } from 'formik';
import { yes_no_options } from '../../../config/config';
import ViewItems from '../../../components/ViewItems';
import { ReactSVG } from 'react-svg';
import icons from '../../../constants/icons';
import { cn, formatNumbers, getContentKeyAccToLang } from '../../../utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { downloadTargetedProductTerms } from '../../../store/actions/accountOpeningAction';
import {
  investment_portfolio_schema,
  investment_portfolio_validation_schema,
} from '../../../validations_schemas/investment_portfolio_validation_schema';
import { isEmptyValue, getRejectedFieldData, isExistingPortfolioChange, isTargetPortfolioChange, numeric_integer_regix } from '../../../utils/functions';

// targetPortfolioActivity id -> targeted-product documentType (1 = Brokerage, 2 = Managed Investments)
const ACTIVITY_TERMS_DOCUMENT_TYPE = {
  1105: 1,
  1104: 2,
};

const InvestmentPortfolio = ({ isKYC, isOpen = true, setStep, contentKey, formRef, percentage }) => {
  const { TextWeight, isArabic } = dynamicfunction();
  const dispatch = useDispatch();

  const corpKycContent = useSelector((state) => state.content?.corpKycContent);
  const corpAOContent = useSelector((state) => state.content?.corpAOContent);
  const { corporate_kyc_data, corporate_dropdown_data, rejection_summary, is_rejected, field_codes } = useSelector((state) => state.corporate);
  const investmentPortfolioData = isKYC ? corporate_kyc_data?.kycInvestmentPortfolio : corporate_kyc_data?.accountOpeningInvestmentPortfolio
  const getContentKey = (key) => {
    const content = isKYC ? corpKycContent : corpAOContent;
    return getContentKeyAccToLang(content?.[contentKey]?.data?.attributes, key, isArabic);
  };

  const rejectedFields = rejection_summary?.find(item => item?.sectionId === 5)?.rejectedFields || [];
  const existingPortfolioRejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'doYouHaveAnExistingPortfolio');
  const outsideKSARejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'hasInstitutionInvestedInFinancialSecuritiesOutsideKSA');
  const debtToInvestmentRatioRejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'whatIsTheInstitutionDebtToInvestmentRatio');
  const investmentObjectiveRejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'whatIsTheInstitutionGeneralInvestmentObjective');
  const preferedCurrenciesRejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'investmentPortfolioPreferredCurrencies');
  const riskAppetiteRejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'whaIsTheInstitutionsRiskAppetite');
  const existingPublicEquityRejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'existingPublicEquity');
  const targetPublicEquityRejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'targetPublicEquity');

  const handleDownloadActivityTerms = (activityId) => {
    const documentType = ACTIVITY_TERMS_DOCUMENT_TYPE[activityId];
    if (!documentType) return;
    dispatch(downloadTargetedProductTerms({ documentType, languageId: isArabic ? 2 : 1 }));
  };

  return (
    <div>
      <ViewReverse className={cn('w-full items-center gap-2 justify-between', isKYC && 'cursor-pointer')} onClick={() => isKYC && setStep(4)}>
        <ViewReverse className="flex items-center gap-2">
          <Text text={getContentKey('investment_portfolio')} weight={TextWeight.headingRegular} className={'text-dark-blue'} size={fontSizes.h30} />
          {rejectedFields?.length > 0 && <ReactSVG src={icons.heading_info_outline_icon} />}
        </ViewReverse>

        <ViewReverse className={'items-center gap-2'}>
          <Text text={`${formatNumbers(percentage) ?? 0}%`} className={'text-dark-blue'} size={fontSizes.h14} />
          {isKYC && <ReactSVG src={icons.downArrow} className={cn(isOpen ? 'rotate-180' : 'rotate-0')} />}
        </ViewReverse>
      </ViewReverse>

      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={investment_portfolio_schema(investmentPortfolioData)}
        validationSchema={investment_portfolio_validation_schema}
        validateOnChange={false}
        validateOnMount={false}
        validateOnBlur={false}
        onSubmit={() => { }}
      >
        {({ values, setFieldValue, errors, setFieldError }) => {
          const existingTotal = (Number(values?.existingPublicEquity) || 0) + (Number(values?.existingPrivateEquity) || 0) + (Number(values?.existingRealEstate) || 0) + (Number(values?.existingPrivateCredit) || 0) + (Number(values?.existingFixedIncomeSukuk) || 0) + (Number(values?.existingMoneyMarketMurabaha) || 0);
          const targetPortfolioTotal = (Number(values?.targetPublicEquity) || 0) + (Number(values?.targetPrivateEquity) || 0) + (Number(values?.targetRealEstate) || 0) + (Number(values?.targetPrivateCredit) || 0) + (Number(values?.targetFixedIncomeSukuk) || 0) + (Number(values?.targetMoneyMarketMurabaha) || 0);
          const handleBreakdownChange = (currentTotal, oldValue, field, text) => {
            if (!(numeric_integer_regix.test(text) || text === '')) return;
            const newValue = text === '' ? 0 : Number(text);
            if (newValue > 100) return;
            if (currentTotal - (Number(oldValue) || 0) + newValue > 100) return;
            setFieldValue(field, text);
          };
          return (
            isOpen && (
              <>
                <Text
                  text={getContentKey('do_you_have_existing_portfolio')} isEmpty={isEmptyValue(values?.doYouHaveAnExistingPortfolio)}
                  className={'text-primary-light mt-8'}
                  size={fontSizes.h14}
                  isRequired
                />
                <ViewReverse className={'gap-6 mt-3'}>
                  {
                    yes_no_options?.map((item) => (
                      <RadioButton
                        label={getContentKey(item?.name)}
                        selected={values?.doYouHaveAnExistingPortfolio === item?.value}
                        onClick={() => {
                          if (values?.doYouHaveAnExistingPortfolio === item?.value) {
                            setFieldValue('doYouHaveAnExistingPortfolio', null);
                          } else {
                            setFieldValue('doYouHaveAnExistingPortfolio', item?.value)
                          }
                          setFieldError('doYouHaveAnExistingPortfolio', null);
                        }}
                        remarksMetadata={values?.doYouHaveAnExistingPortfolio !== investmentPortfolioData?.doYouHaveAnExistingPortfolio ? null : {
                          sectionId: 5,
                          key: 'doYouHaveAnExistingPortfolio',
                        }}
                      />
                    ))
                  }
                </ViewReverse>
                {
                  existingPortfolioRejectedField?.remarks?.length > 0 && values?.doYouHaveAnExistingPortfolio === investmentPortfolioData?.doYouHaveAnExistingPortfolio && (
                    <ViewReverse className='items-center gap-2 mt-2'>
                      <ReactSVG src={icons.field_info_outline} />
                      <ErrorText text={existingPortfolioRejectedField?.remarks} className={'mt-0'} />
                    </ViewReverse>
                  )
                }
                <ErrorText text={getContentKey(errors?.doYouHaveAnExistingPortfolio)} />

                <div className='my-10 h-[1px] bg-black/10' />

                {
                  values?.doYouHaveAnExistingPortfolio && (
                    <>
                      <Text text={getContentKey('current_portfolio')} className={'text-light-blue'} size={fontSizes.h22} />

                      <ViewReverse className={'gap-6 mt-8'} isResponsive>
                        <Dropdown
                          label={getContentKey('value_of_existing_portfolio')}
                          isRequired
                          value={values?.whatIsTheValueOfTheInstitutionsExistingPortfolio ? corporate_dropdown_data?.transactionVolumeInSAR?.find(item => item?.id === values?.whatIsTheValueOfTheInstitutionsExistingPortfolio) : null}
                          options={corporate_dropdown_data?.transactionVolumeInSAR}
                          valueKey={'name'}
                          isRightSymbol
                          onSelect={(val) => {
                            setFieldValue('whatIsTheValueOfTheInstitutionsExistingPortfolio', val?.id);
                          }}
                          isLabelHeight
                          labelDivClass={'md:h-8'}
                          error={getContentKey(errors?.whatIsTheValueOfTheInstitutionsExistingPortfolio)}
                          remarksMetadata={
                            !(existingPortfolioRejectedField?.remarks?.length > 0) &&
                            (values?.whatIsTheValueOfTheInstitutionsExistingPortfolio === investmentPortfolioData?.whatIsTheValueOfTheInstitutionsExistingPortfolio) && {
                              sectionId: 5,
                              key: 'whatIsTheValueOfTheInstitutionsExistingPortfolio',
                            }}
                        />
                        <Dropdown
                          label={getContentKey('how_long_held_investments')}
                          valueKey={'name'}
                          value={corporate_dropdown_data?.investmentHoldingPeriod?.find(item => item?.id === values?.howLongHasTheInstitutionHeldTheseInvestments)}
                          options={corporate_dropdown_data?.investmentHoldingPeriod}
                          onSelect={(val) => setFieldValue('howLongHasTheInstitutionHeldTheseInvestments', val?.id)}
                          isLabelHeight
                          labelDivClass={'md:h-8'}
                          error={getContentKey(errors?.howLongHasTheInstitutionHeldTheseInvestments)}
                          remarksMetadata={
                            !(existingPortfolioRejectedField?.remarks?.length > 0) &&
                            (values?.howLongHasTheInstitutionHeldTheseInvestments === investmentPortfolioData?.howLongHasTheInstitutionHeldTheseInvestments) && {
                              sectionId: 5,
                              key: 'howLongHasTheInstitutionHeldTheseInvestments',
                            }}
                        />
                        <div className='w-full hidden md:flex' />
                      </ViewReverse>

                      <Text
                        text={getContentKey('existing_portfolio_breakdown')} isEmpty={existingTotal === 0}
                        className={'text-primary-light mt-8'}
                        size={fontSizes.h14}
                        isRequired
                      />

                      <ViewReverse>
                        <ViewItems className={'mt-2 w-full lg:w-[653px]'}>
                          <ViewReverse className={'gap-2 w-full justify-between items-center border-t border-b border-grey py-3'}>
                            <Text text={getContentKey('public_equity')} size={fontSizes.h13} />
                            <TableInput
                              value={values?.existingPublicEquity}
                              onChange={(text) => handleBreakdownChange(existingTotal, values?.existingPublicEquity, 'existingPublicEquity', text)}
                              isDisabled={is_rejected && !existingPublicEquityRejectedField?.remarks && !(existingPortfolioRejectedField?.remarks?.length > 0)}
                              placeholder={'0'}
                            />
                          </ViewReverse>
                          <ViewReverse className={'gap-2 w-full justify-between items-center border-b border-grey py-3'}>
                            <Text text={getContentKey('private_equity')} size={fontSizes.h13} />
                            <TableInput
                              value={values?.existingPrivateEquity}
                              onChange={(text) => handleBreakdownChange(existingTotal, values?.existingPrivateEquity, 'existingPrivateEquity', text)}
                              placeholder={'0'}
                              isDisabled={is_rejected && !existingPublicEquityRejectedField?.remarks && !(existingPortfolioRejectedField?.remarks?.length > 0)}
                            />
                          </ViewReverse>
                          <ViewReverse className={'gap-2 w-full justify-between items-center border-b border-grey py-3'}>
                            <Text text={getContentKey('real_estate')} size={fontSizes.h13} />
                            <TableInput
                              value={values?.existingRealEstate}
                              onChange={(text) => handleBreakdownChange(existingTotal, values?.existingRealEstate, 'existingRealEstate', text)}
                              placeholder={'0'}
                              isDisabled={is_rejected && !existingPublicEquityRejectedField?.remarks && !(existingPortfolioRejectedField?.remarks?.length > 0)}
                            />
                          </ViewReverse>
                          <ViewReverse className={'gap-2 w-full justify-between items-center border-b border-grey py-3'}>
                            <Text text={getContentKey('private_credit')} size={fontSizes.h13} />
                            <TableInput
                              value={values?.existingPrivateCredit}
                              onChange={(text) => handleBreakdownChange(existingTotal, values?.existingPrivateCredit, 'existingPrivateCredit', text)}
                              placeholder={'0'}
                              isDisabled={is_rejected && !existingPublicEquityRejectedField?.remarks && !(existingPortfolioRejectedField?.remarks?.length > 0)}
                            />
                          </ViewReverse>
                          <ViewReverse className={'gap-2 w-full justify-between items-center border-b border-grey py-3'}>
                            <Text text={getContentKey('fixed_income_sukuk')} size={fontSizes.h13} />
                            <TableInput
                              value={values?.existingFixedIncomeSukuk}
                              onChange={(text) => handleBreakdownChange(existingTotal, values?.existingFixedIncomeSukuk, 'existingFixedIncomeSukuk', text)}
                              placeholder={'0'}
                              isDisabled={is_rejected && !existingPublicEquityRejectedField?.remarks && !(existingPortfolioRejectedField?.remarks?.length > 0)}
                            />
                          </ViewReverse>
                          <ViewReverse className={'gap-2 w-full justify-between items-center border-b border-black py-3'}>
                            <Text text={getContentKey('money_markets_murabaha')} size={fontSizes.h13} />
                            <TableInput
                              value={values?.existingMoneyMarketMurabaha}
                              onChange={(text) => handleBreakdownChange(existingTotal, values?.existingMoneyMarketMurabaha, 'existingMoneyMarketMurabaha', text)}
                              placeholder={'0'}
                              isDisabled={is_rejected && !existingPublicEquityRejectedField?.remarks && !(existingPortfolioRejectedField?.remarks?.length > 0)}
                            />
                          </ViewReverse>
                          <ViewReverse className={'gap-2 w-full justify-between items-center border-b border-grey py-3'}>
                            <Text text={getContentKey('current_portfolio_total')} size={fontSizes.h13} />
                            <TableInput
                              value={existingTotal}
                              placeholder={'100'}
                              isDisabled
                            />
                          </ViewReverse>
                        </ViewItems>
                      </ViewReverse>
                      {
                        !isExistingPortfolioChange(values, investmentPortfolioData) && existingPublicEquityRejectedField?.remarks?.length > 0 && (
                          <ViewReverse className='items-center gap-2 mt-2'>
                            <ReactSVG src={icons.field_info_outline} />
                            <ErrorText text={existingPublicEquityRejectedField?.remarks} className={'mt-0'} />
                          </ViewReverse>
                        )
                      }
                      <ErrorText text={getContentKey(errors?.existingTotal)} />


                      <Text
                        text={getContentKey('invested_outside_saudi')} isEmpty={isEmptyValue(values?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA)}
                        className={'text-primary-light mt-8'}
                        size={fontSizes.h14}
                        isRequired
                      />
                      <ViewReverse className={'gap-6 mt-3'}>
                        {
                          yes_no_options?.map((item) => (
                            <RadioButton
                              label={getContentKey(item?.name)}
                              selected={values?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA === item?.value}
                              onClick={() => {
                                if (values?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA === item?.value) {
                                  setFieldValue('hasInstitutionInvestedInFinancialSecuritiesOutsideKSA', null);
                                } else {
                                  setFieldValue('hasInstitutionInvestedInFinancialSecuritiesOutsideKSA', item?.value)
                                }
                                setFieldError('hasInstitutionInvestedInFinancialSecuritiesOutsideKSA', null);
                              }}
                              remarksMetadata={
                                !(existingPortfolioRejectedField?.remarks?.length > 0) &&
                                (values?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA === investmentPortfolioData?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA) && {
                                  sectionId: 5,
                                  key: 'hasInstitutionInvestedInFinancialSecuritiesOutsideKSA',
                                }}
                            />
                          ))
                        }
                      </ViewReverse>
                      {
                        outsideKSARejectedField?.remarks?.length > 0 && values?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA === investmentPortfolioData?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA && (
                          <ViewReverse className='items-center gap-2 mt-2'>
                            <ReactSVG src={icons.field_info_outline} />
                            <ErrorText text={outsideKSARejectedField?.remarks} className={'mt-0'} />
                          </ViewReverse>
                        )
                      }
                      <ErrorText text={getContentKey(errors?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA)} />

                      {
                        values?.hasInstitutionInvestedInFinancialSecuritiesOutsideKSA && (
                          <>
                            <Text
                              text={getContentKey('total_size_of_investments_outside_saudi')} isEmpty={isEmptyValue(values?.estimateTotalSizeOfInstitutionInvestmentsInFinancialSecuritieOutsideKSA)}
                              className={'text-primary-light mt-8'}
                              size={fontSizes.h14}
                              isRequired
                            />
                            <ViewReverse className={'gap-6 mt-1'}>
                              <Dropdown
                                valueKey={'name'}
                                value={values?.estimateTotalSizeOfInstitutionInvestmentsInFinancialSecuritieOutsideKSA ? corporate_dropdown_data?.outsideKsa?.find((v) => v?.id === values?.estimateTotalSizeOfInstitutionInvestmentsInFinancialSecuritieOutsideKSA) : null}
                                options={corporate_dropdown_data?.outsideKsa}
                                onSelect={(val) => setFieldValue('estimateTotalSizeOfInstitutionInvestmentsInFinancialSecuritieOutsideKSA', val?.id)}
                                isLabelHeight
                                isRightSymbol
                                remarksMetadata={
                                  (existingPortfolioRejectedField?.remarks?.length > 0 ? false : outsideKSARejectedField?.remarks?.length > 0 ? false : true) &&
                                  (values?.estimateTotalSizeOfInstitutionInvestmentsInFinancialSecuritieOutsideKSA === investmentPortfolioData?.estimateTotalSizeOfInstitutionInvestmentsInFinancialSecuritieOutsideKSA) && {
                                    sectionId: 5,
                                    key: 'estimateTotalSizeOfInstitutionInvestmentsInFinancialSecuritieOutsideKSA',
                                  }}
                              />
                              <div className='w-full hidden md:flex' />
                              <div className='w-full hidden md:flex' />
                            </ViewReverse>

                            <Text
                              text={getContentKey('investment_countries')} isEmpty={isEmptyValue(values?.investmentPortfolioCountries)}
                              className={'text-primary-light mt-8'}
                              size={fontSizes.h14}
                              isRequired
                            />
                            <ViewReverse className={'gap-6 mt-1'}>
                              <Dropdown
                                isMultiSelect
                                value={values?.investmentPortfolioCountries?.map(country => corporate_dropdown_data?.country?.find(item => item?.id === country?.countryId)?.name)?.join(', ')}
                                array={values?.investmentPortfolioCountries?.map(country => country?.countryId)}
                                valueKey={'name'}
                                isClose={false}
                                options={corporate_dropdown_data?.country}
                                onSelect={(country) => {
                                  const exists = values?.investmentPortfolioCountries?.find(item => item?.countryId === country?.id);
                                  if (exists) {
                                    setFieldValue('investmentPortfolioCountries', values?.investmentPortfolioCountries?.filter(item => item?.countryId !== country?.id));
                                  } else {
                                    setFieldValue('investmentPortfolioCountries', [...values?.investmentPortfolioCountries, { countryId: country?.id || 0, portfolioCountryId: country?.id }]);
                                  }
                                }}
                                error={getContentKey(errors?.investmentPortfolioCountries)}
                                remarksMetadata={
                                  (existingPortfolioRejectedField?.remarks?.length > 0 ? false : outsideKSARejectedField?.remarks?.length > 0 ? false : true) &&
                                  values?.investmentPortfolioCountries?.join('') === investmentPortfolioData?.investmentPortfolioCountries?.join('') &&
                                  { sectionId: 5, key: 'investmentPortfolioCountries' }}
                              />
                              <div className='w-full hidden md:flex' />
                              <div className='w-full hidden md:flex' />
                            </ViewReverse>
                          </>
                        )
                      }

                      <Text text={getContentKey('debt_to_investment_ratio')} isEmpty={isEmptyValue(values?.whatIsTheInstitutionsDebtToInvestmentRatio)} className={'text-primary-light mt-10'} size={fontSizes.h14} isRequired />

                      <ViewReverse className={'gap-6 mt-3'} isResponsive>
                        {corporate_dropdown_data?.debtToInvestmentRatio?.map((item) => (
                          <RadioButton
                            key={item?.id}
                            label={item?.name}
                            selected={values?.whatIsTheInstitutionsDebtToInvestmentRatio === item?.id}
                            onClick={() => {
                              if (values?.whatIsTheInstitutionsDebtToInvestmentRatio === item?.id) {
                                setFieldValue('whatIsTheInstitutionsDebtToInvestmentRatio', null);
                              } else {
                                setFieldValue('whatIsTheInstitutionsDebtToInvestmentRatio', item?.id)
                              }
                              setFieldError('whatIsTheInstitutionsDebtToInvestmentRatio', null);
                            }}
                            remarksMetadata={
                              !(existingPortfolioRejectedField?.remarks?.length > 0) &&
                              (values?.whatIsTheInstitutionsDebtToInvestmentRatio === investmentPortfolioData?.whatIsTheInstitutionsDebtToInvestmentRatio) && {
                                sectionId: 5,
                                key: 'whatIsTheInstitutionDebtToInvestmentRatio',
                              }}
                          />
                        ))}
                      </ViewReverse>
                      {
                        debtToInvestmentRatioRejectedField?.remarks?.length > 0 && values?.whatIsTheInstitutionsDebtToInvestmentRatio === investmentPortfolioData?.whatIsTheInstitutionsDebtToInvestmentRatio && (
                          <ViewReverse className='items-center gap-2 mt-2'>
                            <ReactSVG src={icons.field_info_outline} />
                            <ErrorText text={debtToInvestmentRatioRejectedField?.remarks} className={'mt-0'} />
                          </ViewReverse>
                        )
                      }
                      <ErrorText text={getContentKey(errors?.whatIsTheInstitutionsDebtToInvestmentRatio)} />
                      <div className='my-10 h-[1px] bg-black/10' />
                    </>
                  )
                }

                <Text text={getContentKey('target_portfolio')} className={'text-light-blue'} size={fontSizes.h22} />

                {!isKYC && (
                  <>
                    <Text
                      text={getContentKey("activities_like_to_perform")} isEmpty={isEmptyValue(values?.investmentPortfolioAccountActivities)}
                      className={'text-primary-light mt-8'}
                      size={fontSizes.h14}
                      isRequired
                    />
                    <ViewReverse className={'gap-6 mt-3'}>
                      {
                        corporate_dropdown_data?.targetPortfolioActivity?.map((item)=>(
                          <RadioButton
                            label={
                              <Text
                                text={item?.name}
                                className={'underline cursor-pointer'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadActivityTerms(item?.id);
                                }}
                              />
                            }
                            isSquare
                            selected={values?.investmentPortfolioAccountActivities?.find(i => i?.valueId === item?.id)}
                            className={'items-center'}
                            onClick={() => {
                              const exist = values?.investmentPortfolioAccountActivities?.find(i => i?.valueId === item?.id);
                              if (exist) {
                                setFieldValue('investmentPortfolioAccountActivities', values?.investmentPortfolioAccountActivities?.filter(i => i?.valueId !== item?.id));
                              } else {
                                setFieldValue('investmentPortfolioAccountActivities', [...values?.investmentPortfolioAccountActivities, { valueId: item?.id}]);
                              }
                            }}
                            remarksMetadata={values?.investmentPortfolioAccountActivities !== investmentPortfolioData?.investmentPortfolioAccountActivities ? null : {
                              sectionId: 5,
                              key: 'investmentPortfolioAccountActivities',
                            }}
                          />

                        ))
                      }
                    </ViewReverse>
                    <Text
                      text={getContentKey('agree_product_terms')}
                      className={'text-text-grey mt-4'}
                      size={fontSizes.h14}
                    />
                    <ErrorText text={getContentKey(errors?.investmentPortfolioAccountActivities)} />
                  </>
                )}

                <Text
                  text={getContentKey('general_investment_objective')} isEmpty={isEmptyValue(values?.whatIsTheInstitutionsGeneralInvestmentObjective)}
                  className={'text-primary-light mt-10'}
                  size={fontSizes.h14}
                  isRequired
                />
                <ViewReverse className={'gap-6 mt-3'} isResponsive>
                  {corporate_dropdown_data?.generalInvestmentObjective?.map((item) => (
                    <RadioButton
                      key={item?.id}
                      label={item?.name}
                      selected={values?.whatIsTheInstitutionsGeneralInvestmentObjective === item?.id}
                      onClick={() => {
                        if (values?.whatIsTheInstitutionsGeneralInvestmentObjective === item?.id) {
                          setFieldValue('whatIsTheInstitutionsGeneralInvestmentObjective', null);
                        } else {
                          setFieldValue('whatIsTheInstitutionsGeneralInvestmentObjective', item?.id)
                        }
                        setFieldError('whatIsTheInstitutionsGeneralInvestmentObjective', null);
                      }}
                      remarksMetadata={values?.whatIsTheInstitutionsGeneralInvestmentObjective !== investmentPortfolioData?.whatIsTheInstitutionsGeneralInvestmentObjective ? null : {
                        sectionId: 5,
                        key: 'whatIsTheInstitutionGeneralInvestmentObjective',
                      }}
                    />
                  ))}
                </ViewReverse>
                {
                  investmentObjectiveRejectedField?.remarks?.length > 0 && values?.whatIsTheInstitutionsGeneralInvestmentObjective === investmentPortfolioData?.whatIsTheInstitutionsGeneralInvestmentObjective && (
                    <ViewReverse className='items-center gap-2 mt-2'>
                      <ReactSVG src={icons.field_info_outline} />
                      <ErrorText text={investmentObjectiveRejectedField?.remarks} className={'mt-0'} />
                    </ViewReverse>
                  )
                }
                <ErrorText text={getContentKey(errors?.whatIsTheInstitutionsGeneralInvestmentObjective)} />

                <ViewReverse className={'mt-10 gap-6'} isResponsive>
                  <ViewItems className={'w-full'}>
                    <Text
                      text={getContentKey('preferred_investment_currencies')} isEmpty={isEmptyValue(values?.investmentPortfolioPreferredCurrencies)}
                      className={'text-primary-light'}
                      size={fontSizes.h14}
                      isRequired
                    />
                    <ViewReverse className={'gap-6 mt-3 w-full'}>
                      {
                        corporate_dropdown_data?.preferredInvestmentCurrencies?.map((item) => (
                          <RadioButton
                            label={item?.name}
                            isSquare
                            selected={values?.investmentPortfolioPreferredCurrencies?.find(i => i?.valueId === item?.id)}
                            className={'items-center'}
                            onClick={() => {
                              const exists = values?.investmentPortfolioPreferredCurrencies?.find(i => i?.valueId === item?.id);
                              if (exists) {
                                setFieldValue('investmentPortfolioPreferredCurrencies', values?.investmentPortfolioPreferredCurrencies?.filter(i => i?.valueId !== item?.id));
                              } else {
                                setFieldValue('investmentPortfolioPreferredCurrencies', [...values?.investmentPortfolioPreferredCurrencies, { valueId: item?.id, portfolioPreferredCurrencyId: item?.id }]);
                              }
                            }}
                            remarksMetadata={values?.investmentPortfolioPreferredCurrencies?.map(item => item?.valueId)?.join('') == investmentPortfolioData?.investmentPortfolioPreferredCurrencies?.map(item => item?.valueId)?.join('') && {
                              sectionId: 5,
                              key: 'investmentPortfolioPreferredCurrencies',
                            }}
                          />
                        ))
                      }
                    </ViewReverse>
                    {
                      preferedCurrenciesRejectedField?.remarks?.length > 0 && values?.investmentPortfolioPreferredCurrencies?.map(item => item?.valueId)?.join('') == investmentPortfolioData?.investmentPortfolioPreferredCurrencies?.map(item => item?.valueId)?.join('') && (
                        <ViewReverse className='items-center gap-2 mt-2'>
                          <ReactSVG src={icons.field_info_outline} />
                          <ErrorText text={preferedCurrenciesRejectedField?.remarks} className={'mt-0'} />
                        </ViewReverse>
                      )
                    }
                    <ErrorText text={getContentKey(errors?.investmentPortfolioPreferredCurrencies)} />
                  </ViewItems>

                  {
                    values?.investmentPortfolioPreferredCurrencies?.find(i => i?.valueId === 790) && (
                      <Dropdown
                        label={getContentKey('foreign_currencies')}
                        isRequired
                        isMultiSelect
                        value={values?.investmentPortfolioForeignCurrencies?.map(currency => corporate_dropdown_data?.foreignCurrencies?.find(item => item?.id === currency?.valueId)?.name)?.join(', ')}
                        array={values?.investmentPortfolioForeignCurrencies?.map(currency => currency?.valueId)}
                        valueKey={'name'}
                        isClose={false}
                        options={corporate_dropdown_data?.foreignCurrencies}
                        onSelect={(currency) => {
                          const exists = values?.investmentPortfolioForeignCurrencies?.find(item => item?.valueId === currency?.id);
                          if (exists) {
                            setFieldValue('investmentPortfolioForeignCurrencies', values?.investmentPortfolioForeignCurrencies?.filter(item => item?.valueId !== currency?.id));
                          } else {
                            setFieldValue('investmentPortfolioForeignCurrencies', [...values?.investmentPortfolioForeignCurrencies, { valueId: currency?.id }]);
                          }
                          setFieldError('investmentPortfolioForeignCurrencies', null);
                        }}
                        isLabelHeight
                        labelDivClass={'md:h-12 lg:h-12'}
                        error={getContentKey(errors?.investmentPortfolioForeignCurrencies?.[0]?.valueId)}
                        remarksMetadata={!(preferedCurrenciesRejectedField?.remarks?.length > 0) &&
                          (values?.investmentPortfolioForeignCurrencies?.join('') === investmentPortfolioData?.investmentPortfolioForeignCurrencies?.join('')) && {
                          sectionId: 5,
                          key: 'ifForeignCurrenciesPleaseSpecifyWhichOnes',
                        }}
                      />
                    )
                  }

                  <Dropdown
                    label={getContentKey('expected_time_horizon')}
                    valueKey={'name'}
                    value={values?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments ? corporate_dropdown_data?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments?.find((v) => v?.id === values?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments) : null}
                    options={corporate_dropdown_data?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments}
                    onSelect={(val) => setFieldValue('whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments', val?.id)}
                    isLabelHeight
                    error={getContentKey(errors?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments)}
                    labelDivClass={'md:h-12 lg:h-12'}
                    remarksMetadata={values?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments !== investmentPortfolioData?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments ? null : {
                      sectionId: 5,
                      key: 'whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments',
                    }}
                  />
                  {/* <InputField
                    label={getContentKey('expected_time_horizon')}
                    placeholder={getContentKey('institution_expected_time_placeholder')}
                    isRequired
                    value={values?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments}
                    onChange={(text) => {
                      if (numeric_integer_regix.test(text) || text === '') {
                        setFieldValue('whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments', text)
                      }
                    }}
                    isLabelHeight
                    labelDivClass={'md:h-12 lg:h-12'}
                    error={getContentKey(errors?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments)}
                    remarksMetadata={values?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments !== investmentPortfolioData?.whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments ? null : {
                      sectionId: 5,
                      key: 'whatIsInstitutionsExpectedTimeHorizonForRedeemingTheseInvestments',
                    }}
                  /> */}
                </ViewReverse>

                <Text text={getContentKey('institute_risk_appetite')} isEmpty={isEmptyValue(values?.whaIsTheInstitutionsRiskAppetite)} className={'text-primary-light mt-10'} size={fontSizes.h14} isRequired />
                <ViewReverse className={'gap-6 mt-3'} isResponsive>
                  {corporate_dropdown_data?.riskAppetite?.map((item) => (
                    <RadioButton
                      key={item?.id}
                      label={item?.name}
                      selected={values?.whaIsTheInstitutionsRiskAppetite === item?.id}
                      onClick={() => {
                        if (values?.whaIsTheInstitutionsRiskAppetite === item?.id) {
                          setFieldValue('whaIsTheInstitutionsRiskAppetite', null);
                        } else {
                          setFieldValue('whaIsTheInstitutionsRiskAppetite', item?.id)
                        }
                        setFieldError('whaIsTheInstitutionsRiskAppetite', null);
                        if (item?.id !== 786) {
                          setFieldValue('targetPublicEquity', '')
                          setFieldValue('targetPrivateEquity', '')
                          setFieldValue('targetRealEstate', '')
                          setFieldValue('targetPrivateCredit', '')
                          setFieldValue('targetFixedIncomeSukuk', '')
                          setFieldValue('targetMoneyMarketMurabaha', '')
                        }
                      }}
                      subText={item?.description}
                      subTextColor={'text-dark-grey/70'}
                      subTextClasses={'md:max-w-48'}
                      className={'items-start'}
                      remarksMetadata={values?.whaIsTheInstitutionsRiskAppetite !== investmentPortfolioData?.whaIsTheInstitutionsRiskAppetite ? null : {
                        sectionId: 5,
                        key: 'whaIsTheInstitutionsRiskAppetite',
                      }}
                    />
                  ))}
                </ViewReverse>
                {
                  riskAppetiteRejectedField?.remarks?.length > 0 && values?.whaIsTheInstitutionsRiskAppetite === investmentPortfolioData?.whaIsTheInstitutionsRiskAppetite && (
                    <ViewReverse className='items-center gap-2 mt-2'>
                      <ReactSVG src={icons.field_info_outline} />
                      <ErrorText text={riskAppetiteRejectedField?.remarks} className={'mt-0'} />
                    </ViewReverse>
                  )
                }
                <ErrorText text={getContentKey(errors?.whaIsTheInstitutionsRiskAppetite)} />

                <Text
                  text={getContentKey('target_investment_breakdown')} isEmpty={!isEmptyValue(values?.whaIsTheInstitutionsRiskAppetite) && targetPortfolioTotal === 0}
                  className={'text-primary-light mt-10 mb-2'}
                  size={fontSizes.h14}
                  isRequired
                />

                <ViewReverse>
                  <ViewItems className={'w-full lg:w-[653px]'}>
                    <div className='h-[1px] w-full bg-grey mt-2' />
                    <Text
                      text={getContentKey('institution_risk_appetite_info')}
                      size={fontSizes.h14}
                      isRequired
                      className={'my-3'}
                    />
                    <div className='h-[1px] w-full bg-grey' />
                  </ViewItems>
                </ViewReverse>

                <ViewReverse className={'max-w-[83vw] lg:w-full overflow-x-auto'}>
                  <div className={'min-w-[653px] lg:max-w-[653px]'}>
                    <ViewReverse className={'gap-2 min-w-full justify-between items-center border-b border-grey py-3'}>
                      <div className='min-w-48' />
                      <ViewReverse className={'gap-9'}>
                        <Text text={`${getContentKey('low_risk')}`} size={fontSizes.h13} className={'min-w-28'} />
                        <Text text={`${getContentKey('medium')}`} size={fontSizes.h13} className={'min-w-28'} />
                        <Text text={`${getContentKey('high')}`} size={fontSizes.h13} className={'min-w-28'} />
                      </ViewReverse>
                    </ViewReverse>
                    <ViewReverse className={'gap-2 min-w-full justify-between items-center border-b border-grey py-3'}>
                      <Text text={getContentKey('public_equity')} size={fontSizes.h13} className={'min-w-48'} />
                      <ViewReverse className={'gap-9'}>
                        <TableCrossView />
                        <TableCrossView />
                        <TableInput
                          value={values?.targetPublicEquity}
                          onChange={(text) => handleBreakdownChange(targetPortfolioTotal, values?.targetPublicEquity, 'targetPublicEquity', text)}
                          placeholder={'0'}
                          isDisabled={values?.whaIsTheInstitutionsRiskAppetite !== 786 || (is_rejected && !targetPublicEquityRejectedField?.remarks && !(riskAppetiteRejectedField?.remarks?.length > 0))}
                        />
                      </ViewReverse>
                    </ViewReverse>
                    <ViewReverse className={'gap-2 min-w-full justify-between items-center border-b border-grey py-3'}>
                      <Text text={getContentKey('private_equity')} size={fontSizes.h13} className={'min-w-48'} />
                      <ViewReverse className={'gap-9'}>
                        <TableCrossView />
                        <TableCrossView />
                        <TableInput
                          value={values?.targetPrivateEquity}
                          onChange={(text) => handleBreakdownChange(targetPortfolioTotal, values?.targetPrivateEquity, 'targetPrivateEquity', text)}
                          placeholder={'0'}
                          isDisabled={values?.whaIsTheInstitutionsRiskAppetite !== 786 || (is_rejected && !targetPublicEquityRejectedField?.remarks && !(riskAppetiteRejectedField?.remarks?.length > 0))}
                        />
                      </ViewReverse>
                    </ViewReverse>
                    <ViewReverse className={'gap-2 min-w-full justify-between items-center border-b border-grey py-3'}>
                      <Text text={getContentKey('real_estate')} size={fontSizes.h13} className={'min-w-48'} />
                      <ViewReverse className={'gap-9'}>
                        <TableCrossView />
                        <TableCrossView />
                        <TableInput
                          value={values?.targetRealEstate}
                          onChange={(text) => handleBreakdownChange(targetPortfolioTotal, values?.targetRealEstate, 'targetRealEstate', text)}
                          placeholder={'0'}
                          isDisabled={values?.whaIsTheInstitutionsRiskAppetite !== 786 || (is_rejected && !targetPublicEquityRejectedField?.remarks && !(riskAppetiteRejectedField?.remarks?.length > 0))}
                        />
                      </ViewReverse>
                    </ViewReverse>
                    <ViewReverse className={'gap-2 min-w-full justify-between items-center border-b border-grey py-3'}>
                      <Text text={getContentKey('private_credit')} size={fontSizes.h13} className={'min-w-48'} />
                      <ViewReverse className={'gap-9'}>
                        <TableCrossView />
                        <TableCrossView />
                        <TableInput
                          value={values?.targetPrivateCredit}
                          onChange={(text) => handleBreakdownChange(targetPortfolioTotal, values?.targetPrivateCredit, 'targetPrivateCredit', text)}
                          placeholder={'0'}
                          isDisabled={values?.whaIsTheInstitutionsRiskAppetite !== 786 || (is_rejected && !targetPublicEquityRejectedField?.remarks && !(riskAppetiteRejectedField?.remarks?.length > 0))}
                        />
                      </ViewReverse>
                    </ViewReverse>
                    <ViewReverse className={'gap-2 min-w-full justify-between items-center border-b border-grey py-3'}>
                      <Text text={getContentKey('fixed_income_sukuk')} size={fontSizes.h13} className={'min-w-48'} />
                      <ViewReverse className={'gap-9'}>
                        <TableCrossView />
                        <TableCrossView />
                        <TableInput
                          value={values?.targetFixedIncomeSukuk}
                          onChange={(text) => handleBreakdownChange(targetPortfolioTotal, values?.targetFixedIncomeSukuk, 'targetFixedIncomeSukuk', text)}
                          placeholder={'0'}
                          isDisabled={values?.whaIsTheInstitutionsRiskAppetite !== 786 || (is_rejected && !targetPublicEquityRejectedField?.remarks && !(riskAppetiteRejectedField?.remarks?.length > 0))}
                        />
                      </ViewReverse>
                    </ViewReverse>
                    <ViewReverse className={'gap-2 min-w-full justify-between items-center border-b border-black py-3'}>
                      <Text text={getContentKey('money_markets_murabaha')} size={fontSizes.h13} className={'min-w-48'} />
                      <ViewReverse className={'gap-9'}>
                        <TableInput
                          value={values?.whaIsTheInstitutionsRiskAppetite === 788 ? values?.targetMoneyMarketMurabaha : ''}
                          onChange={(text) => handleBreakdownChange(targetPortfolioTotal, values?.targetMoneyMarketMurabaha, 'targetMoneyMarketMurabaha', text)}
                          placeholder={'0'}
                          isDisabled={values?.whaIsTheInstitutionsRiskAppetite !== 788 || (is_rejected && !targetPublicEquityRejectedField?.remarks && !(riskAppetiteRejectedField?.remarks?.length > 0))}
                        />
                        <TableInput
                          value={values?.whaIsTheInstitutionsRiskAppetite === 787 ? values?.targetMoneyMarketMurabaha : ''}
                          onChange={(text) => handleBreakdownChange(targetPortfolioTotal, values?.targetMoneyMarketMurabaha, 'targetMoneyMarketMurabaha', text)}
                          placeholder={'0'}
                          isDisabled={values?.whaIsTheInstitutionsRiskAppetite !== 787 || (is_rejected && !targetPublicEquityRejectedField?.remarks && !(riskAppetiteRejectedField?.remarks?.length > 0))}
                        />
                        <TableInput
                          value={values?.whaIsTheInstitutionsRiskAppetite === 786 ? values?.targetMoneyMarketMurabaha : ''}
                          onChange={(text) => handleBreakdownChange(targetPortfolioTotal, values?.targetMoneyMarketMurabaha, 'targetMoneyMarketMurabaha', text)}
                          placeholder={'0'}
                          isDisabled={values?.whaIsTheInstitutionsRiskAppetite !== 786 || (is_rejected && !targetPublicEquityRejectedField?.remarks && !(riskAppetiteRejectedField?.remarks?.length > 0))}
                        />
                      </ViewReverse>
                    </ViewReverse>
                    <ViewReverse className={'gap-2 min-w-full justify-between items-center py-3'}>
                      <Text text={getContentKey('target_portfolio_total')} size={fontSizes.h13} className={'min-w-48'} />
                      <ViewReverse className={'gap-9'}>
                        <TableInput value={values?.whaIsTheInstitutionsRiskAppetite === 788 ? targetPortfolioTotal : 0} placeholder={'0'} isDisabled />
                        <TableInput value={values?.whaIsTheInstitutionsRiskAppetite === 787 ? targetPortfolioTotal : 0} placeholder={'0'} isDisabled />
                        <TableInput value={values?.whaIsTheInstitutionsRiskAppetite === 786 ? targetPortfolioTotal : 0} placeholder={'0'} isDisabled />
                      </ViewReverse>
                    </ViewReverse>
                  </div>
                </ViewReverse>
                {
                  !isTargetPortfolioChange(values, investmentPortfolioData) && targetPublicEquityRejectedField?.remarks?.length > 0 && (
                    <ViewReverse className='items-center gap-2 mt-2'>
                      <ReactSVG src={icons.field_info_outline} />
                      <ErrorText text={targetPublicEquityRejectedField?.remarks} className={'mt-0'} />
                    </ViewReverse>
                  )
                }
                <ErrorText text={getContentKey(errors?.targetTotal)} />

                <ViewReverse>
                  <TextArea
                    label={getContentKey('other_financial_information')}
                    placeholder={getContentKey('enter_here')}
                    divClasses={'mt-10 w-full lg:w-2/3 '}
                    isLabelHeight
                    isArabicAllowed
                    labelDivClass={'md:h-6 lg:h-8'}
                    maxLength={90}
                    value={values?.isThereAnyOtherFinancialInformationThatYouWouldLikeToDiscloseOnBehalfOfTheInstitution}
                    onChange={(text) => {
                      setFieldValue('isThereAnyOtherFinancialInformationThatYouWouldLikeToDiscloseOnBehalfOfTheInstitution', text)
                    }}
                    error={getContentKey(errors?.isThereAnyOtherFinancialInformationThatYouWouldLikeToDiscloseOnBehalfOfTheInstitution)}
                    remarksMetadata={values?.isThereAnyOtherFinancialInformationThatYouWouldLikeToDiscloseOnBehalfOfTheInstitution !== investmentPortfolioData?.isThereAnyOtherFinancialInformationThatYouWouldLikeToDiscloseOnBehalfOfTheInstitution ? null : {
                      sectionId: 5,
                      key: 'isThereAnyOtherFinancialInformationThatYouWouldLikeToDiscloseOnBehalfOfTheInstitution',
                    }}
                  />
                </ViewReverse>

                {!isKYC && (
                  <>
                    <Text
                      text={getContentKey("preferred_relationship_manager")}
                      className={'text-primary-light mt-10'}
                      size={fontSizes.h14}
                    />
                    <ViewReverse className={'gap-6 mt-1'}>
                      <InputField
                        placeholder={getContentKey('name')}
                        value={values?.relationshipManagerName}
                        onChange={(text) => {
                          setFieldValue('relationshipManagerName', text)
                        }}
                        maxLength={50}
                        error={getContentKey(errors?.relationshipManagerName)}
                        remarksMetadata={values?.relationshipManagerName !== investmentPortfolioData?.relationshipManagerName ? null : {
                          sectionId: 5,
                          key: 'relationshipManagerName',
                        }}
                      />
                      <div className='w-full hidden md:flex' />
                      <div className='w-full hidden md:flex' />
                    </ViewReverse>
                  </>
                )}
              </>
            )
          )
        }}
      </Formik>
    </div >
  );
};

export default InvestmentPortfolio;
